/**
 * @module engine/hitTest
 * @summary Find the shape / handle / connection under a world point.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 3367-3414.
 */
import { S } from './state';
import type { Shape, Connection, Handle } from '../types';
import { getHandles, segDist } from './geometry';
import { connGeom } from './routing';

/**
 * The shape under (wx, wy), or null.
 * // INVARIANT: iterate the array in REVERSE — later index = drawn on top =
 * clicked first. zIndex is intentionally ignored (imported diagrams often have
 * every zIndex = 1). Three passes: strict-inset regular shapes, then exact
 * bbox for tiny symbols, then zoneboxes/textlabels last.
 */
export function hitShape(wx: number, wy: number): Shape | null {
  const INSET = 4;
  // Pass 1: inset hit on regular shapes (avoids false hits from symbols whose
  //         art spills slightly outside their declared bbox).
  for (let i = S.shapes.length - 1; i >= 0; i--) {
    const s = S.shapes[i];
    if (s.type === 'zonebox' || s.type === 'textlabel') continue;
    if (wx >= s.x + INSET && wx <= s.x + s.w - INSET && wy >= s.y + INSET && wy <= s.y + s.h - INSET)
      return s;
  }
  // Pass 2: exact bbox for small symbols.
  for (let i = S.shapes.length - 1; i >= 0; i--) {
    const s = S.shapes[i];
    if (s.type === 'zonebox' || s.type === 'textlabel') continue;
    if (wx >= s.x && wx <= s.x + s.w && wy >= s.y && wy <= s.y + s.h) return s;
  }
  // Pass 3: zoneboxes + textlabels last.
  for (let i = S.shapes.length - 1; i >= 0; i--) {
    const s = S.shapes[i];
    if (wx >= s.x && wx <= s.x + s.w && wy >= s.y && wy <= s.y + s.h) return s;
  }
  return null;
}

/** The resize handle of shape `s` under (wx, wy), or null. */
export function hitHandle(s: Shape, wx: number, wy: number, slop = 7): Handle | null {
  return getHandles(s).find((h) => Math.abs(h.x - wx) < slop && Math.abs(h.y - wy) < slop) || null;
}

/** The connection whose path passes near (wx, wy), or null. */
export function hitConn(wx: number, wy: number, slop = 7): Connection | null {
  for (const cn of S.connections) {
    const g = connGeom(cn);
    if (!g) continue;
    const { pts } = g;
    for (let i = 0; i < pts.length - 1; i++) {
      if (segDist({ x: wx, y: wy }, pts[i], pts[i + 1]) < slop) return cn;
    }
  }
  return null;
}

export interface ConnSegHit {
  cn: Connection;
  segIdx: number;
  pts: { x: number; y: number }[];
  hx: number;
  hy: number;
}

/** A draggable segment-midpoint handle on a SELECTED connection, or null. */
export function hitConnSegHandle(wx: number, wy: number, slop = 8): ConnSegHit | null {
  for (const cn of S.connections) {
    if (!S.selected.includes(cn.id)) continue;
    const g = connGeom(cn);
    if (!g) continue;
    const { pts } = g;
    for (let si = 0; si < pts.length - 1; si++) {
      const hx = (pts[si].x + pts[si + 1].x) / 2;
      const hy = (pts[si].y + pts[si + 1].y) / 2;
      if (Math.abs(wx - hx) < slop && Math.abs(wy - hy) < slop) {
        return { cn, segIdx: si, pts, hx, hy };
      }
    }
  }
  return null;
}
